import { Button, Card, Chip, DeleteIcon, EditIcon, TabBar } from "@bp/ui-components";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "urql";
import { graphql } from "../../client/gql";
import { useSetCrumbTitle } from "../../contexts/CrumbTitleContext";
import { OmniOrgView } from "../../components/Organizations/OmniOrgView/OmniOrgView";
import { OrgFormModal } from "../../components/Organizations/modals/OrgFormModal";
import { showInfoToast } from "../../utils/toast";
import { GroupsPanel } from "../../components/Organizations/tabs/GroupsPanel";
import { MembersPanel } from "../../components/Organizations/tabs/MembersPanel";
import { ProductsTab } from "../../components/Organizations/tabs/ProductsTab";
import { OrgMark } from "../../components/OrgMark/OrgMark";
import { useAuthClaims } from "../../hooks/useAuthClaims";
import prim from "../../core/styles/helpers.module.scss";

export type OrgTabKey = "modules" | "members" | "groups";

// The detail query owns the Organization node and spreads each child's
// colocated fragment so the tabs / edit modal receive exactly what they render.
const OrganizationDetailPageQuery = graphql(`
  query OrganizationDetailPage_Query($where: OrganizationWhere) {
    organizations(where: $where) {
      uuid
      name
      zipCode
      city
      region
      country
      profilesConnection {
        totalCount
      }
      federatedClientConnection {
        totalCount
        edges {
          node {
            name
            label
          }
        }
      }
      ...ProductsTab_Organization
      ...MembersPanel_Organization
      ...GroupsPanel_Organization
      ...OrgFormModal_Organization
    }
  }
`);

export function OrganizationDetailPage() {
  const { orgId } = useParams();
  const { pimAuthClaims } = useAuthClaims();
  const [tab, setTab] = useState<OrgTabKey>("modules");
  const [editing, setEditing] = useState(false);
  const [{ data }] = useQuery({
    query: OrganizationDetailPageQuery,
    variables: { where: { uuid: orgId } },
  });
  const org = data?.organizations?.[0];

  let omniClaim: ReturnType<typeof pimAuthClaims.getOmniOrganization> | null = null;
  try {
    omniClaim = pimAuthClaims.getOmniOrganization();
  } catch {
    omniClaim = null;
  }
  const omniUuid = omniClaim?.uuid ?? null;
  const omniName = (omniClaim as { name?: string } | null)?.name;
  // the omni org is a claims-only virtual org — not returned by the org query
  const isOmni = !!omniUuid && orgId === omniUuid;
  const displayName = org?.name ?? (isOmni ? (omniName ?? "Omni-Admin") : undefined);

  useSetCrumbTitle(displayName);
  useEffect(() => {
    const previous = document.title;
    document.title = `${displayName ?? "Organisation"} · Schulsuite-Admin`;
    return () => {
      document.title = previous;
    };
  }, [displayName]);

  if (isOmni && omniUuid) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-4)" }}>
        <Card contentPadding="none">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-4)",
              padding: "var(--spacing-5)",
            }}
          >
            <OrgMark org={{ system: true }} size={56} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-2)",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 18, letterSpacing: "-0.01em", color: "var(--color-text)" }}>
                {displayName}
              </span>
              <Chip value="System" isSmall />
            </div>
          </div>
        </Card>
        <OmniOrgView org={{ uuid: omniUuid, name: displayName ?? "Omni-Admin" }} />
      </div>
    );
  }

  if (!org) {
    return <p className={prim.muted}>Organisation nicht gefunden.</p>;
  }
  const location =
    [org.zipCode, org.city, org.region, org.country].filter(Boolean).join(" · ") || undefined;
  const sso = org.federatedClientConnection?.edges?.[0]?.node;
  const hasSso = (org.federatedClientConnection?.totalCount ?? 0) > 0;

  // The DS TabBar renders the active tab's `content` itself (and only mounts
  // the active one), which is also what drives the active-tab highlight.
  const tabs = [
    { id: "modules", trigger: "Produkte & Zugriff", content: <ProductsTab org={org} /> },
    { id: "members", trigger: "Mitglieder", content: <MembersPanel org={org} /> },
    { id: "groups", trigger: "Gruppen", content: <GroupsPanel org={org} /> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-4)" }}>
      <Card contentPadding="none">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--spacing-4)",
            flexWrap: "wrap",
            padding: "var(--spacing-5)",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: "var(--spacing-4)", minWidth: 0 }}
          >
            <OrgMark org={{ system: false }} size={56} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 18, letterSpacing: "-0.01em", color: "var(--color-text)" }}>
                {org.name}
              </div>
              {location && (
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--color-grey)",
                    marginTop: "var(--spacing-1)",
                  }}
                >
                  {location}
                </div>
              )}
              <div
                style={{ display: "flex", gap: 6, marginTop: "var(--spacing-2)", flexWrap: "wrap" }}
              >
                <Chip value={`${org.profilesConnection?.totalCount ?? 0} Mitglieder`} isSmall />
                <Chip
                  value={hasSso ? sso?.name || sso?.label || "SSO" : "Lokale Anmeldung"}
                  isSmall
                />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "var(--spacing-2)", flexShrink: 0 }}>
            <Button hierarchy="secondary" icon={<EditIcon />} onClick={() => setEditing(true)}>
              Bearbeiten
            </Button>
            {/* Stub on purpose: deleting an organization must also propagate the
                removal to the products (TK-Planung, Bildungsplattform), which is
                not wired up — so we surface why instead of calling
                deleteOrganizations and leaving the products inconsistent. */}
            <Button
              hierarchy="tertiary"
              icon={<DeleteIcon />}
              onClick={() =>
                showInfoToast(
                  "Löschen ist noch nicht möglich: Die Organisation müsste dabei auch in den Produkten (TK-Planung, Bildungsplattform) entfernt werden.",
                )
              }
            >
              Löschen
            </Button>
          </div>
        </div>
      </Card>

      {/* omni org is handled by the early return above — this path is always a real org */}
      <TabBar tabs={tabs} activeTabId={tab} onValueChange={(value) => setTab(value as OrgTabKey)} />
      {editing && (
        <OrgFormModal
          org={org}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
    </div>
  );
}
